import React from "react";
import { FileViewerContainer } from "components/common";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceConstants } from "utils/constants";
import { useInvoiceService } from "serviceHooks";
import { FileEnums } from "models/enums";
import { LocalizationHelperInstance } from "utils/helpers";
import { TranslationKeys } from "translations";
import { useSelector } from "react-redux";
import { AppState } from "store";

function useInvoiceFileViewer(
  invoice: WintSuperkollAPIModel.Invoice | undefined,
  preventDownload?: boolean,
  overrideHtml?: string
) {
  const invoiceService = useInvoiceService();
  const [attachments, setAttachments] = React.useState<ImageMeta[]>();
  const selectedCompanyName = useSelector((store: AppState) => store.auth.selectedCompany.name);

  React.useEffect(() => {
    if (invoice) {
      const attachments =
        invoice.attachments?.map((meta) => ({
          id: meta.id ?? "",
          contentType: meta.contentType ?? "",
          numberOfPages: 1,
          data: meta.data,
          loading: (meta as unknown as ImageMeta).loading,
        })) ?? [];

      if (invoice.invoiceSource === WintSuperkollAPIModel.InvoiceSource.SelfInvoice) {
        setAttachments(attachments);
        return;
      }

      if (invoice.status !== WintSuperkollAPIModel.InvoiceStatus.NotSent) {
        setAttachments(
          Array.from(new Array(invoice.pDFCount))
            .map(
              (_, index): ImageMeta => ({
                id: `${index}`,
                contentType: FileEnums.ContentTypes.ApplicationPDF,
                numberOfPages: 1,
                fileName: `${LocalizationHelperInstance.getFormattedMessage(
                  TranslationKeys.Common.Invoice2
                )}-${selectedCompanyName}-${invoice.serialNumber}`,
              })
            )
            .concat(attachments)
        );
      } else {
        setAttachments(
          [
            {
              id: InvoiceConstants.InvoiceHtmlId,
              contentType: "html",
              numberOfPages: 1,
              data: overrideHtml,
            },
          ].concat(attachments)
        );
      }
    }
  }, [invoice, overrideHtml, invoice?.attachments]);

  const pdfCache = React.useRef(new Map<string, Promise<string>>());

  const getFile = (attachmentId: number | string, index: number) => {
    if (attachmentId === InvoiceConstants.InvoiceHtmlId) {
      return overrideHtml
        ? Promise.resolve(overrideHtml)
        : invoiceService.getInvoicePDFPreview(invoice!.id).then((res) => res.data as string);
    } else {
      const invoicePdfCount = invoice!.pDFCount ?? 1;
      if (index < invoicePdfCount) {
        // The same invoice PDF is requested again by every viewer that mounts (e.g. a dialog beside the page).
        const cacheKey = `${invoice!.id}-${index}`;
        const cached = pdfCache.current.get(cacheKey);
        if (cached) return cached;
        const request = invoiceService
          .getInvoicePdf(invoice!.id, invoicePdfCount - index - 1)
          .then((res) => res.data as string);
        pdfCache.current.set(cacheKey, request);
        request.catch(() => pdfCache.current.delete(cacheKey));
        return request;
      } else {
        return invoiceService.getAttachment(invoice!.id, attachmentId as string);
      }
    }
  };

  const fileViewerComponent = React.useMemo(
    () => (
      <FileViewerContainer
        key={invoice?.id}
        meta={attachments}
        parentId={invoice?.id ?? 0}
        loading={invoice === undefined || attachments === undefined}
        getFile={getFile}
        preventDownload={preventDownload}
      />
    ),
    [attachments, invoice?.id]
  );

  return { fileViewerComponent, attachments };
}

export default useInvoiceFileViewer;
